import React, { useMemo, useState, useEffect } from "react"
import { Source } from "./mapLibre/Source"
import { Layer } from "./mapLibre/Layer"
import { findRoute2g, route2gEq, RouteStop } from "@/reducers/routes2gSlice"
import * as polyline from "@mapbox/polyline"
import { useMapContext } from "@/components/maps/mapLibre/MapContext"
import { useAppSelector } from "@/reducers/hooks"

type DriverCurrentRouteProps = {
  stops: RouteStop[]
  routeJid: string
}

/**
 * Displays the driver's current route (original stops) with distinctive green styling.
 * Rendered above route lines and hoverpoints but below waypoint icons so stops remain visible.
 */
const DriverCurrentRoute: React.FC<DriverCurrentRouteProps> = ({
  stops,
  routeJid,
}) => {
  const mapContext = useMapContext()
  const map = mapContext?.getMapInstance()
  const [waypointLayerExists, setWaypointLayerExists] = useState(false)

  // Check if waypoint-icon layer exists before rendering
  useEffect(() => {
    if (!map) return

    const checkLayer = () => {
      if (!map.style) return
      const layerExists = !!map.getLayer("maplibre-gl-directions-waypoint-icon")
      setWaypointLayerExists(layerExists)
    }

    // Check immediately
    checkLayer()

    // Also listen for style load events in case layers are added later
    map.on("styledata", checkLayer)

    return () => {
      map.off("styledata", checkLayer)
    }
  }, [map])

  // Get the current route from Redux to determine original stops
  const currentRoute = useAppSelector(findRoute2g(routeJid), route2gEq)

  // Extract original stop IDs from the current route
  const originalStopIds = useMemo(() => {
    if (!currentRoute?.stops) return new Set<string>()
    return new Set(
      currentRoute.stops
        .map((stop) => stop.id)
        .filter((id): id is string => id !== undefined),
    )
  }, [currentRoute])

  // Filter to only include original stops and collect their polylines
  const routeGeometry = useMemo(() => {
    const coordinates: [number, number][] = []

    stops.forEach((stop, index) => {
      // Only include segments where the destination stop is original
      if (stop.id && originalStopIds.has(stop.id) && stop.polyline) {
        try {
          const decoded = polyline.decode(stop.polyline)
          // Convert from [lat, lng] to [lng, lat] for GeoJSON
          const coords = decoded.map(
            (coord) => [coord[1], coord[0]] as [number, number],
          )
          coordinates.push(...coords)
        } catch (error) {
          console.error("Error decoding polyline:", error)
        }
      }
    })

    if (coordinates.length === 0) {
      return null
    }

    return {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "LineString" as const,
        coordinates,
      },
    }
  }, [stops, originalStopIds])

  // Hide the green hoverpoint (drag dot) when mouse is over the driver's current route
  useEffect(() => {
    if (!map || !routeGeometry || !waypointLayerExists) return

    // After the map is removed (e.g. map type switch) `map.style` is undefined
    // and any style access throws, so bail out early.
    const isMapAlive = () => !!map.style

    const hideHoverpoint = () => {
      if (!isMapAlive()) return
      if (map.getLayer("maplibre-gl-directions-hoverpoint")) {
        map.setLayoutProperty(
          "maplibre-gl-directions-hoverpoint",
          "visibility",
          "none",
        )
      }
      if (map.getLayer("maplibre-gl-directions-hoverpoint-casing")) {
        map.setLayoutProperty(
          "maplibre-gl-directions-hoverpoint-casing",
          "visibility",
          "none",
        )
      }
    }

    const showHoverpoint = () => {
      if (!isMapAlive()) return
      if (map.getLayer("maplibre-gl-directions-hoverpoint")) {
        map.setLayoutProperty(
          "maplibre-gl-directions-hoverpoint",
          "visibility",
          "visible",
        )
      }
      if (map.getLayer("maplibre-gl-directions-hoverpoint-casing")) {
        map.setLayoutProperty(
          "maplibre-gl-directions-hoverpoint-casing",
          "visibility",
          "visible",
        )
      }
    }

    map.on("mouseenter", "driver-current-route-casing", hideHoverpoint)
    map.on("mouseleave", "driver-current-route-casing", showHoverpoint)

    return () => {
      map.off("mouseenter", "driver-current-route-casing", hideHoverpoint)
      map.off("mouseleave", "driver-current-route-casing", showHoverpoint)
      // Restore visibility on cleanup
      showHoverpoint()
    }
  }, [map, routeGeometry, waypointLayerExists])

  // Don't render until waypoint-icon layer exists to avoid console errors
  if (!waypointLayerExists || !routeGeometry) return null

  return (
    <Source id="driver-current-route" type="geojson" data={routeGeometry}>
      {/* Outer green casing - thicker to match image */}
      <Layer
        id="driver-current-route-casing"
        type="line"
        paint={{
          "line-color": "#4CAF50",
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 15, 16, 20],
          "line-opacity": 1,
        }}
        layout={{
          "line-cap": "round",
          "line-join": "round",
        }}
        beforeId="maplibre-gl-directions-waypoint-icon"
      />

      {/* White middle stroke */}
      <Layer
        id="driver-current-route-white"
        type="line"
        paint={{
          "line-color": "#FFFFFF",
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 8, 16, 13],
          "line-opacity": 1,
        }}
        layout={{
          "line-cap": "round",
          "line-join": "round",
        }}
        beforeId="maplibre-gl-directions-waypoint-icon"
      />
    </Source>
  )
}

export default DriverCurrentRoute
